/**
 * SmartEdge - opt-in obstacle-avoiding orthogonal edge.
 *
 * This is an ADDITIVE edge type registered as "smart". It does NOT replace or
 * modify the default "custom" edge. Existing diagrams keep using "custom" and
 * render exactly as before; an edge only routes around obstacles when its
 * `type` is explicitly set to "smart" (via the Properties panel).
 *
 * It reads the absolute bounding boxes of all other nodes from the React Flow
 * store and asks the pure `computeOrthogonalRoute` helper for an axis-aligned
 * polyline that avoids those boxes, then renders it with the built-in
 * `<BaseEdge />`. Edge styling (color, width, dash, line cap/join, opacity,
 * animation, arrowheads, label) is read from `edge.data` using the SAME
 * conventions as CustomEdge so switching routing modes preserves the visual
 * style — including arrowheads and animation — as faithfully as possible.
 */

import { memo } from 'react'
import {
  BaseEdge,
  EdgeLabelRenderer,
  useStore,
  type EdgeProps,
} from '@xyflow/react'
import {
  computeOrthogonalRoute,
  polylineToSvgPath,
  polylineMidpoint,
  type Rect,
} from '../../utils/orthogonalRoute'
import {
  generateArrowMarker,
  getArrowMarkerRefs,
  type ArrowType,
} from '../../utils/edge-markers'

const LINE_STYLES: Record<string, string | undefined> = {
  solid: undefined,
  dashed: '8 8',
  dotted: '2 4',
  longdash: '16 8',
}

interface SmartEdgeData {
  lineStyle?: keyof typeof LINE_STYLES
  lineWidth?: number
  lineCap?: 'butt' | 'round' | 'square'
  lineJoin?: 'miter' | 'round' | 'bevel'
  color?: string
  opacity?: number
  arrowStart?: ArrowType
  arrowEnd?: ArrowType
  arrowSize?: number
  bidirectional?: boolean
  routePadding?: number
  labelFontSize?: number
  labelBackground?: string
  labelBackgroundColor?: string
}

/**
 * Minimal shape of the bits of an internal node we read for obstacle geometry.
 * Avoids depending on `ReactFlowState`, which @xyflow/react does not re-export
 * from its package root.
 */
interface InternalNodeLike {
  id: string
  position: { x: number; y: number }
  width?: number | null
  height?: number | null
  measured?: { width?: number; height?: number }
  internals?: { positionAbsolute?: { x: number; y: number } }
}

interface StoreSlice {
  nodeLookup: Map<string, InternalNodeLike>
}

/**
 * Select the obstacle rectangles (absolute coords) for every node except the
 * edge's own source and target. Memoised via a stable selector so the edge only
 * re-renders when node geometry actually changes.
 */
function makeObstacleSelector(sourceId: string, targetId: string) {
  return (state: StoreSlice): Rect[] => {
    const rects: Rect[] = []
    state.nodeLookup.forEach((node) => {
      if (node.id === sourceId || node.id === targetId) return
      const pos = node.internals?.positionAbsolute ?? node.position
      const width = node.measured?.width ?? node.width ?? 0
      const height = node.measured?.height ?? node.height ?? 0
      if (!width || !height) return
      rects.push({ x: pos.x, y: pos.y, width, height })
    })
    return rects
  }
}

function rectsEqual(a: Rect[], b: Rect[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].x !== b[i].x ||
      a[i].y !== b[i].y ||
      a[i].width !== b[i].width ||
      a[i].height !== b[i].height
    ) {
      return false
    }
  }
  return true
}

function SmartEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style = {},
  label,
  data,
  animated,
  selected,
}: EdgeProps) {
  const edgeData = data as SmartEdgeData | undefined

  const obstacles = useStore(makeObstacleSelector(source, target), rectsEqual)

  const route = computeOrthogonalRoute(
    { x: sourceX, y: sourceY },
    { x: targetX, y: targetY },
    obstacles,
    { padding: edgeData?.routePadding ?? 14 }
  )

  const path = polylineToSvgPath(route)
  const mid = polylineMidpoint(route)

  // Read styling with the SAME conventions / defaults as CustomEdge.
  const baseColor = edgeData?.color ?? '#b1b1b7'
  const baseWidth = edgeData?.lineWidth ?? 2
  const color = selected ? '#3b82f6' : baseColor
  // Match CustomEdge's selected emphasis (baseWidth + 4).
  const width = selected ? baseWidth + 4 : baseWidth
  const lineStyle = (edgeData?.lineStyle ?? 'solid') as keyof typeof LINE_STYLES
  const lineCap = (edgeData?.lineCap ?? 'butt') as 'butt' | 'round' | 'square'
  const lineJoin = (edgeData?.lineJoin ?? 'miter') as 'miter' | 'round' | 'bevel'
  const opacity = edgeData?.opacity ?? 1
  const arrowStart = (edgeData?.arrowStart ?? 'none') as ArrowType
  const arrowEnd = (edgeData?.arrowEnd ?? 'arrow') as ArrowType
  const arrowSize = edgeData?.arrowSize ?? 1
  const bidirectional = edgeData?.bidirectional === true
  const isAnimated = animated === true

  const labelFontSize = edgeData?.labelFontSize ?? 12
  const labelBackground = edgeData?.labelBackground ?? 'white'
  const labelBackgroundColor = edgeData?.labelBackgroundColor ?? '#ffffff'

  // Generate marker defs + url() refs identically to CustomEdge.
  const { markerStart, markerEnd } = getArrowMarkerRefs(
    id,
    arrowStart,
    arrowEnd,
    bidirectional
  )

  return (
    <>
      {/* SVG Marker Definitions - unique per edge, same ids as CustomEdge */}
      <defs>
        {(bidirectional || arrowStart !== 'none') &&
          generateArrowMarker(
            id,
            arrowStart === 'none' ? 'arrow' : arrowStart,
            'start',
            color,
            opacity,
            arrowSize
          )}

        {arrowEnd !== 'none' &&
          generateArrowMarker(id, arrowEnd, 'end', color, opacity, arrowSize)}
      </defs>

      <BaseEdge
        id={id}
        path={path}
        markerStart={markerStart}
        markerEnd={markerEnd}
        className={`${isAnimated ? 'edge-animated' : ''} ${isAnimated ? `edge-style-${lineStyle}` : ''}`}
        style={{
          ...style,
          stroke: color,
          strokeWidth: width,
          strokeDasharray: LINE_STYLES[lineStyle],
          strokeLinecap: lineCap,
          strokeLinejoin: lineJoin,
          opacity,
          fill: 'none',
          filter: selected ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))' : undefined,
        }}
      />

      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${mid.x}px,${mid.y}px)`,
              fontSize: labelFontSize,
              fontWeight: 500,
              background:
                labelBackground === 'none'
                  ? 'transparent'
                  : labelBackground === 'custom'
                    ? labelBackgroundColor
                    : 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              border:
                labelBackground === 'none'
                  ? 'none'
                  : selected
                    ? '2px solid #3b82f6'
                    : '1px solid #d0d0d0',
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            {String(label)}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  )
}

export default memo(SmartEdge)
